import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    FaBuilding, FaPlus, FaPen, FaGlobe, FaStar, FaRegStar, FaArchive, FaUndo, FaSlidersH, FaChartBar, FaFolderOpen,
} from "react-icons/fa";
import { COMPANIES_CONSTANTS } from "../_MODULES/_API";
import { CompanyFormModal } from "../_UI/CompanyFormModal";
import { PageHeader } from "../../../../../_CORE/APP/Templates/_UI/PageHeader/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { Card, EmptyState, Skeleton, Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { FieldDate, FieldInput, FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { ViewLayout } from "../../../../_UI/ViewLayout";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { onApiAction } from "../../../../_UI/onApiAction";
import { onSilentPatch } from "../../../../_UI/onSilentPatch";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { AdvancedFilterPanel, onDateMatches } from "../../../../_UI/AdvancedFilterPanel";
import { usePagedList } from "../../../../_UI/usePagedList";
import { StatisticsModal } from "../../../../_UI/StatisticsModal";
import "../_UI/_Styles/Companies.css";

const COMPANY_SCOPE_OPTIONS = [
    { value: "ACTIVE", label: "Compañías activas" },
    { value: "ARCHIVED", label: "Compañías archivadas" },
];

export const CompaniesViewMain = () => {
    const [useSearch, setSearch] = useState("");
    const [useCompanyScope, setCompanyScope] = useState("ACTIVE");
    const [statisticsOpen, setStatisticsOpen] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [advanced, setAdvanced] = useState({ favorite: "ALL", website: "", project: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });
    const [useCompanyEdit, setCompanyEdit] = useState(null);
    const [useIsModalOpen, setIsModalOpen] = useState(false);
    const [useActiveCompanies, setActiveCompanies] = useState([]);
    const [useArchivedCompanies, setArchivedCompanies] = useState([]);

    const activeQuery = useApiQuery({ minTimeQuery: 300, enablePagination: false, startLoading: true });
    const archivedQuery = useApiQuery({ minTimeQuery: 250, enablePagination: false });

    useEffect(() => {
        activeQuery.apiGet({ endpoint: COMPANIES_CONSTANTS.LIST });
        archivedQuery.apiGet({ endpoint: COMPANIES_CONSTANTS.ARCHIVE_LIST });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { setActiveCompanies(activeQuery.useApiData ?? []); }, [activeQuery.useApiData]);
    useEffect(() => { setArchivedCompanies(archivedQuery.useApiData ?? []); }, [archivedQuery.useApiData]);

    const SOURCE = useCompanyScope === "ARCHIVED" ? useArchivedCompanies : useActiveCompanies;

    const ITEMS = useMemo(() => [...SOURCE].sort((a, b) =>
        (Number(!!b.isFavorite) - Number(!!a.isFavorite)) || String(a.name ?? "").localeCompare(String(b.name ?? ""))
    ), [SOURCE]);

    const VISIBLE = ITEMS
        .filter((company) => advanced.favorite === "ALL" || (!!company.isFavorite === (advanced.favorite === "YES")))
        .filter((company) => !String(advanced.website || "").trim() || onMatchesSearch(advanced.website, [company.website, company.contactEmail, company.contactPhone]))
        .filter((company) => !String(advanced.project || "").trim() || onMatchesSearch(advanced.project, (company.projects ?? []).flatMap((project) => [project.name, project.description, ...(project.stack || [])])))
        .filter((company) => onDateMatches(advanced.dateField === "CREATED" ? company.createdAt : company.updatedAt, advanced.dateRange, advanced.dateExact))
        .filter((company) => onMatchesSearch(useSearch, [
            company.name, company.description, company.website, company.contactEmail,
            ...(company.projects ?? []).map((project) => project.name),
        ]));
    const advancedCount = [advanced.favorite !== "ALL", String(advanced.website || "").trim(), String(advanced.project || "").trim(), advanced.dateRange?.[0] || advanced.dateRange?.[1], advanced.dateExact].filter(Boolean).length;
    const clearAdvanced = () => setAdvanced({ favorite: "ALL", website: "", project: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });

    const PAGER = usePagedList(VISIBLE, 6, `companies-${useCompanyScope}`);

    const onOpenCreate = () => {
        setCompanyEdit(null);
        setIsModalOpen(true);
    };

    const onOpenEdit = (company) => {
        setCompanyEdit(company);
        setIsModalOpen(true);
    };

    const onPatchCompanyLocal = (id, patch) => {
        setActiveCompanies((rows) => rows.map((row) => row.id === id ? { ...row, ...patch } : row));
        setArchivedCompanies((rows) => rows.map((row) => row.id === id ? { ...row, ...patch } : row));
    };

    const onToggleFavorite = async (company) => {
        const previous = !!company.isFavorite;
        const next = !previous;
        onPatchCompanyLocal(company.id, { isFavorite: next });

        const RESPONSE = await onSilentPatch({
            endpoint: COMPANIES_CONSTANTS.UPDATE,
            id: company.id,
            data: { isFavorite: next },
            errorMessage: "No se pudo guardar el destacado de la compañía.",
        });

        if (!RESPONSE.success) onPatchCompanyLocal(company.id, { isFavorite: previous });
    };

    const onArchive = async (company) => {
        const projectCount = company.projects?.length ?? company.metrics?.totalProjects ?? 0;
        const ok = await confirm.danger(
            "Archivar compañía",
            `Se archivará “${company.name}”${projectCount > 0 ? ` junto con sus ${projectCount} proyectos` : ""}. Podés restaurarla desde Compañías archivadas.`,
        );
        if (!ok) return;

        setActiveCompanies((rows) => rows.filter((row) => row.id !== company.id));
        setArchivedCompanies((rows) => [{ ...company, archivedAt: new Date().toISOString() }, ...rows]);

        const response = await onApiAction({
            method: "PATCH",
            endpoint: COMPANIES_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: company.id },
            successMessage: "Compañía archivada",
        });

        if (!response.success) {
            setArchivedCompanies((rows) => rows.filter((row) => row.id !== company.id));
            setActiveCompanies((rows) => [company, ...rows]);
        }
    };

    const onRestore = async (company) => {
        setArchivedCompanies((rows) => rows.filter((row) => row.id !== company.id));
        setActiveCompanies((rows) => [{ ...company, archivedAt: null }, ...rows]);

        const response = await onApiAction({
            method: "PATCH",
            endpoint: COMPANIES_CONSTANTS.RESTORE_ONE,
            pathParams: { id: company.id },
            successMessage: "Compañía restaurada",
        });

        if (!response.success) {
            setActiveCompanies((rows) => rows.filter((row) => row.id !== company.id));
            setArchivedCompanies((rows) => [company, ...rows]);
        }
    };

    const IS_LOADING = useCompanyScope === "ARCHIVED" ? archivedQuery.useApiLoading : activeQuery.useApiLoading;

    return (
        <ViewLayout
            header={<PageHeader
                title={useCompanyScope === "ARCHIVED" ? "Compañías archivadas" : "Compañías"}
                description={useCompanyScope === "ARCHIVED"
                    ? "Restaurá una compañía desde acá. La eliminación definitiva queda centralizada en Archivados."
                    : "Cada compañía agrupa sus propios proyectos."}
                icon={<FaBuilding />}
                actions={<>
                    <AppButton variant="outline" tone="neutral" icon={<FaChartBar />} isIconOnly title="Estadísticas" aria-label="Estadísticas" onClick={() => setStatisticsOpen(true)} />
                    {useCompanyScope === "ACTIVE" && <AppButton icon={<FaPlus />} onClick={onOpenCreate}>Nueva compañía</AppButton>}
                </>} />}
            toolbar={<ViewToolbar
                filters={<>
                    <SearchFilter value={useSearch} onChange={setSearch} total={ITEMS.length} matches={VISIBLE.length}
                        placeholder="Ej.: Global Bridge Solutions o Sistema nuevo CRM..." />
                    <FieldSelect id="companyScope" label={null} options={COMPANY_SCOPE_OPTIONS}
                        value={useCompanyScope} onChange={(event) => setCompanyScope(event.target.value)} />
                    <AppButton variant={advancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />} onClick={() => setAdvancedOpen((current) => !current)}>{advancedCount ? `Avanzado (${advancedCount})` : "Avanzado"}</AppButton>
                </>}
                expandedContent={<AdvancedFilterPanel isOpen={advancedOpen} activeCount={advancedCount} onClear={clearAdvanced}>
                    <FieldSelect id="companies_adv_favorite" label="Destacado" value={advanced.favorite} onChange={(event) => setAdvanced((current) => ({ ...current, favorite: event.target.value }))} options={[{ value: "ALL", label: "Todos" }, { value: "YES", label: "Destacadas" }, { value: "NO", label: "No destacadas" }]} />
                    <FieldInput id="companies_adv_website" label="Sitio / contacto" value={advanced.website} onChange={(event) => setAdvanced((current) => ({ ...current, website: event.target.value }))} />
                    <FieldInput id="companies_adv_project" label="Proyecto / stack" value={advanced.project} onChange={(event) => setAdvanced((current) => ({ ...current, project: event.target.value }))} />
                    <FieldSelect id="companies_adv_date" label="Fecha" value={advanced.dateField} onChange={(event) => setAdvanced((current) => ({ ...current, dateField: event.target.value }))} options={[{ value: "UPDATED", label: "Última actualización" }, { value: "CREATED", label: "Creación" }]} />
                    <FieldDate id="companies_adv_from" label="Desde" value={advanced.dateRange?.[0] || null} onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [date || null, current.dateRange?.[1] || null], dateExact: null }))} isClearable />
                    <FieldDate id="companies_adv_to" label="Hasta" value={advanced.dateRange?.[1] || null} onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [current.dateRange?.[0] || null, date || null], dateExact: null }))} isClearable />
                    <FieldDate id="companies_adv_exact" label="Fecha exacta" value={advanced.dateExact} onChange={(date) => setAdvanced((current) => ({ ...current, dateExact: date || null, dateRange: date ? [null, null] : current.dateRange }))} isClearable />
                </AdvancedFilterPanel>}
                total={PAGER.total}
                perPage={PAGER.usePerPage}
                onChangePerPage={PAGER.onChangePerPage}
                exportColumns={[
                    { key: "name", label: "Compañía" },
                    { key: "website", label: "Sitio" },
                    { key: "contactEmail", label: "Correo" },
                    { key: "contactPhone", label: "Teléfono" },
                    { key: "projects", label: "Proyectos" },
                ]}
                exportRows={VISIBLE.map((company) => ({
                    name: company.name,
                    website: company.website ?? "",
                    contactEmail: company.contactEmail ?? "",
                    contactPhone: company.contactPhone ?? "",
                    projects: company.projects?.length ?? company.metrics?.totalProjects ?? 0,
                }))}
                exportName={useCompanyScope === "ARCHIVED" ? "companias-archivadas" : "companias"} /> }>

            {IS_LOADING && (
                <div className="LStudio__app_companies_grid">
                    <Skeleton variant="box" height={150} />
                    <Skeleton variant="box" height={150} />
                    <Skeleton variant="box" height={150} />
                </div>
            )}

            {!IS_LOADING && VISIBLE.length === 0 && (
                <EmptyState icon={useCompanyScope === "ARCHIVED" ? <FaArchive /> : <FaBuilding />}
                    title={useCompanyScope === "ARCHIVED" ? "No hay compañías archivadas" : "Todavía no cargaste ninguna compañía"}
                    description={useCompanyScope === "ARCHIVED"
                        ? "Cuando archives una compañía va a aparecer acá."
                        : "Creá la primera para poder empezar a cargar proyectos."}
                    action={useCompanyScope === "ACTIVE"
                        ? <AppButton icon={<FaPlus />} onClick={onOpenCreate}>Nueva compañía</AppButton>
                        : null} />
            )}

            {!IS_LOADING && VISIBLE.length > 0 && (
                <div className="LStudio__app_companies_grid">
                    {PAGER.paged.map((company) => (
                        <Card key={company.id} className="LStudio__app_companies_card" data-favorite={!!company.isFavorite}>
                            <div className="LStudio__app_companies_card_head">
                                <span className="LStudio__app_companies_card_dot"
                                    style={{ background: company.color || "var(--LS__accent, #e83a52)" }} />

                                {useCompanyScope === "ACTIVE" ? (
                                    <Link to={`/companies/${company.id}`} className="LStudio__app_companies_card_name">
                                        {company.isFavorite && <FaStar className="LStudio__app_record_star" title="Destacada" />}
                                        {company.name}
                                    </Link>
                                ) : (
                                    <span className="LStudio__app_companies_card_name">
                                        {company.isFavorite && <FaStar className="LStudio__app_record_star" title="Destacada" />}
                                        {company.name}
                                    </span>
                                )}

                                <Badge tone={useCompanyScope === "ARCHIVED" ? "neutral" : (company.isActive ? "ok" : "neutral")} size="sm">
                                    {useCompanyScope === "ARCHIVED" ? "Archivada" : (company.isActive ? "Activa" : "Inactiva")}
                                </Badge>

                                <CardActionMenu title="Acciones de la compañía" items={useCompanyScope === "ARCHIVED" ? [
                                    { key: "favorite", icon: company.isFavorite ? <FaStar /> : <FaRegStar />,
                                        label: company.isFavorite ? "Quitar de destacados" : "Destacar", onClick: () => onToggleFavorite(company) },
                                    { key: "edit", icon: <FaPen />, label: "Editar", onClick: () => onOpenEdit(company) },
                                    { key: "restore", icon: <FaUndo />, label: "Restaurar", onClick: () => onRestore(company) },
                                    { divider: true },
                                ] : [
                                    { key: "favorite", icon: company.isFavorite ? <FaStar /> : <FaRegStar />,
                                        label: company.isFavorite ? "Quitar de destacados" : "Destacar", onClick: () => onToggleFavorite(company) },
                                    { key: "edit", icon: <FaPen />, label: "Editar", onClick: () => onOpenEdit(company) },
                                    { divider: true },
                                    { key: "archive", icon: <FaArchive />, label: "Archivar", tone: "danger", onClick: () => onArchive(company) },
                                ]} />
                            </div>

                            <p className="LStudio__app_companies_card_desc">{company.description || "Sin descripción"}</p>

                            <div className="LStudio__app_companies_card_meta">
                                <span>{company.projects?.length ?? company.metrics?.totalProjects ?? 0} proyectos</span>
                                {company.website && (
                                    <a href={company.website} target="_blank" rel="noreferrer"
                                        className="LStudio__app_companies_card_link">
                                        <FaGlobe /> <span>{company.website.replace(/^https?:\/\//, "")}</span>
                                    </a>
                                )}
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            <ViewPagination page={PAGER.usePage} totalPages={PAGER.totalPages}
                total={PAGER.total} perPage={PAGER.usePerPage} onChange={PAGER.onChangePage} />

            <StatisticsModal isOpen={statisticsOpen} onClose={() => setStatisticsOpen(false)}
                title="Estadísticas · Compañías" description="Resumen general de las compañías y sus proyectos."
                metrics={[
                    { key: "active", label: "Activas", value: useActiveCompanies.length, icon: <FaBuilding />, tone: "accent", isLoading: activeQuery.useApiLoading },
                    { key: "archived", label: "Archivadas", value: useArchivedCompanies.length, icon: <FaArchive />, tone: "neutral", isLoading: archivedQuery.useApiLoading },
                    { key: "favorite", label: "Destacadas", value: [...useActiveCompanies, ...useArchivedCompanies].filter((company) => company.isFavorite).length, icon: <FaStar />, tone: "neutral" },
                    { key: "projects", label: "Proyectos", value: useActiveCompanies.reduce((total, company) => total + (company.projects?.length ?? company.metrics?.totalProjects ?? 0), 0), icon: <FaFolderOpen />, tone: "neutral" },
                ]} />

            <CompanyFormModal
                isOpen={useIsModalOpen}
                company={useCompanyEdit}
                companies={[...useActiveCompanies, ...useArchivedCompanies]}
                onClose={() => setIsModalOpen(false)}
                onSaved={(saved, isEdit) => {
                    const current = [...useActiveCompanies, ...useArchivedCompanies].find((row) => row.id === saved.id);
                    const normalized = { ...current, ...saved, projects: saved.projects ?? current?.projects ?? [] };
                    if (isEdit) {
                        onPatchCompanyLocal(saved.id, normalized);
                        return;
                    }
                    setActiveCompanies((rows) => [normalized, ...rows]);
                }} />

        </ViewLayout>
    );
};
